import { useSearchParams } from "react-router-dom";
import useRoles from "../../features/Roles/hooks/useRoles";
import { useEffect, useState } from "react";
import {
  Button,
  Center,
  Flex,
  Heading,
  Box,
  Text,
  Spinner,
  TableContainer,
  Table as ChakraTable,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Input,
  Textarea
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { OlaggModule, PermissionEnum, PurchaseRewardExclusion } from "@olagg/db-types";
import Pagination from "@olagg/ui-kit/Pagination";
import usePurchaseRewardExclusions from "./hooks/usePurchaseRewardExclusions";
import moment from "moment";
import OlaggFormSelect from "../../components/Forms/OlaggFormSelect";
import { useForm, FormProvider } from "react-hook-form";
import { fetcher, AdminProductsEndpoints } from "@olagg/api-hooks";

const Actions = ({ exclusion, onDeleted }: { exclusion: PurchaseRewardExclusion; onDeleted: () => void }) => {
  const { missingPermission } = useRoles();
  const { deleteExclusion } = usePurchaseRewardExclusions();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (window.confirm('¿Estás seguro que deseas eliminar esta exclusión?')) {
      setIsDeleting(true);
      try {
        await deleteExclusion(exclusion.id);
        await onDeleted();
      } catch (error) {
        console.error("Error eliminando exclusión:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  }

  const canDelete = !missingPermission(OlaggModule.PurchaseRewardExclusion, PermissionEnum.DELETE);

  return (
    <Box display="flex" gap="10px" flexWrap="wrap">
      <Button
        isDisabled={!canDelete || isDeleting}
        colorScheme="red"
        size="sm"
        onClick={handleDelete}
        isLoading={isDeleting}
        loadingText="Eliminando..."
      >
        Eliminar
      </Button>
    </Box>
  )
}

const PurchaseRewardExclusionsList = () => {
  const { missingPermission } = useRoles();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getExclusions, createExclusion, loading } = usePurchaseRewardExclusions();
  const [exclusions, setExclusions] = useState<PurchaseRewardExclusion[]>([]);
  const [count, setCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [productOptions, setProductOptions] = useState<{value: string, label: string}[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [reason, setReason] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const methods = useForm();

  const [filter, setFilter] = useState({
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 10)
  });
  const { page, limit } = filter;

  const onFilter = ({
    name: filterName,
    value
  }: { name: string, value: any }) => {
    const parsedValue = Number(value);
    setFilter({ ...filter, [filterName]: parsedValue });

    if (value) {
      searchParams.set(filterName, String(value));
    } else {
      searchParams.delete(filterName);
    }
    setSearchParams(searchParams);
  };

  const refreshExclusions = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const fetchExclusions = async () => {
    try {
      const response = await getExclusions({ limit, page });
      setExclusions(response.purchaseRewardExclusions);
      setCount(response.meta.count);
    } catch (error) {
      console.error('Error fetching exclusions:', error);
    }
  };

  const loadProducts = async (inputValue: string) => {
    try {
      const response = await fetcher(AdminProductsEndpoints.all({
        page: 1,
        limit: 100,
        name: inputValue || undefined
      }));
      const options = response.products.map((p: any) => ({
        value: p.uid,
        label: `${p.name} (${p.category})`
      }));
      setProductOptions(options);
      return options;
    } catch (error) {
      console.error('Error loading products:', error);
      return [];
    }
  };

  useEffect(() => {
    loadProducts('');
  }, []);

  useEffect(() => {
    fetchExclusions();
  }, [page, limit, refreshTrigger]);

  const handleCreate = async () => {
    if (!selectedProduct) {
      toast({
        title: 'Error',
        description: 'Debes seleccionar un producto',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCreating(true);
    try {
      await createExclusion({
        product_uid: selectedProduct,
        reason: reason || undefined
      });
      toast({
        title: 'Exclusión creada',
        description: 'El producto ha sido excluido de las recompensas',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setSelectedProduct('');
      setReason('');
      onClose();
      refreshExclusions();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear la exclusión',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full'>
        <Heading textAlign="center" mb={6}>
          Productos Excluidos de Recompensas
        </Heading>

        <Flex w='full' alignItems='center' gap='5'>
          <Button
            mt='2' mb='5'
            display='flex'
            variant="outlinedGradient"
            leftIcon={<FaPlus />}
            onClick={onOpen}
            isDisabled={missingPermission(OlaggModule.PurchaseRewardExclusion, PermissionEnum.CREATE)}
          >
            Agregar Exclusión
          </Button>
        </Flex>

        <Text fontSize="sm" color="gray.400" mb={4}>
          Los productos en esta lista no serán elegibles para ninguna promoción de recompensa por compra.
        </Text>

        {loading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          exclusions?.length > 0 ? (
            <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
              <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                  <ChakraTable
                    variant="striped"
                    size="sm"
                    colorScheme="olaggPurple"
                    fontSize="18px"
                    position='relative'
                    whiteSpace={'normal'}
                  >
                    <Thead>
                      <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
                        <Th py={'20px'} color="white">Producto</Th>
                        <Th py={'20px'} color="white">Categoría</Th>
                        <Th py={'20px'} color="white">Razón</Th>
                        <Th py={'20px'} color="white">Fecha de exclusión</Th>
                        <Th py={'20px'} color="white">Acciones</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {exclusions?.map((exclusion, index) => (
                        <Tr key={index} color="white">
                          <Td py={'20px'}>
                            <Text fontWeight="bold">{exclusion.product.name}</Text>
                            <Text fontSize="xs" color="gray.400">{exclusion.product.uid}</Text>
                          </Td>
                          <Td py={'20px'}>{exclusion.product.category}</Td>
                          <Td py={'20px'}>
                            {exclusion.reason || <Text color="gray.400">Sin razón especificada</Text>}
                          </Td>
                          <Td py={'20px'}>{moment(exclusion.createdAt).format('DD/MM/YYYY HH:mm')}</Td>
                          <Td py={'20px'}>
                            <Actions exclusion={exclusion} onDeleted={refreshExclusions} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </ChakraTable>
                </TableContainer>
              </Flex>
            </Flex>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>No hay productos excluidos</Text>
            </Box>
          )
        )}

        <Pagination
          params={{ limit, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={val =>
            onFilter({
              name: 'limit',
              value: val
            })
          }
          hideLimitInput
        />

        {/* Modal para crear exclusión */}
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent bg="gray.800">
            <ModalHeader color="white">Agregar Exclusión de Producto</ModalHeader>
            <ModalCloseButton color="white" />
            <ModalBody>
              <FormProvider {...methods}>
                <Box mb={4}>
                  <Text color="white" fontSize="sm" mb={2}>Producto</Text>
                  <OlaggFormSelect
                    controlName='product_uid'
                    placeholder='Selecciona un producto'
                    staticOptions={productOptions}
                    asyncOptions={loadProducts}
                    isSearchable
                    required={true}
                    onChange={(value) => setSelectedProduct(value?.value || value)}
                  />
                </Box>
              </FormProvider>
              <Box>
                <Text color="white" fontSize="sm" mb={2}>Razón (opcional)</Text>
                <Textarea
                  placeholder="Razón de la exclusión..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  bg="gray.700"
                  color="white"
                  borderColor="gray.600"
                />
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose} color="white">
                Cancelar
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleCreate}
                isLoading={isCreating}
                loadingText="Creando..."
              >
                Crear Exclusión
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Center>
  )
}

export default PurchaseRewardExclusionsList;
